<template>
  <div>
    <div class="activity-stage face-flip">
      <div v-if="currentRound.steps">
        <div ref="faceCards" class="top">
          <div
            v-for="(step, index) in currentRound.steps"
            :key="step.imageLocation"
            transition="kwtStagger"
            class="card-item"
          >
            <flippable-card :is-flipped="index === progress.stepIndex || showAllCards">
              <div slot="card-back" class="back-side">
                <img :src="backOfCardPath" />
              </div>
              <div slot="card-front" class="front-side">
                <img :src="step.imageLocation" />
              </div>
            </flippable-card>
          </div>
        </div>
        <div class="bottom">
          <div
            v-for="(step, index) in validStepsForTyping"
            :key="`text_${index}`"
            class="td-container"
          >
            <blinking-cursor-text
              v-if="step.stepIndex === progress.stepIndex"
              transition="slide-in"
              :text="step.answer"
              :text-step="step.stepIndex"
            ></blinking-cursor-text>
          </div>
        </div>
      </div>
    </div>
    <slot name="keyboard"></slot>
  </div>
</template>

<script>
import { TweenMax, TimelineMax } from 'gsap';

import backOfCardPath from 'images/face-flip-card-back.png';

import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';
import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';

import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import FlippableCard from 'components/activity/FlippableCard.vue';
import kwtStagger from 'utils/transitions/StaggerTransition';

export default {
  name: 'FaceFlip',

  components: {
    BlinkingCursorText,
    FlippableCard
  },

  transitions: {
    kwtStagger
  },

  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],

  data() {
    return {
      showAllCards: false,
      backOfCardPath
    };
  },

  computed: {
    validStepsForTyping() {
      return this.currentRound.steps.filter((step, index) => index >= this.progress.stepIndex);
    }
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  methods: {
    async loadActivity() {
      this.createRounds(this.getActivityState());

      await this.loadImages();
    },

    getActivityState() {
      const { rounds } = this.activityData;
      return {
        rounds,
        instructions: {
          written: 'Type each sentence.',
          voice: `${this.voiceInstructionsPrefix}/faceflip-${this.getRandomVoice()}-1.mp3`,
          delay: 2
        }
      };
    },

    async loadImages() {
      this.queueImage(backOfCardPath);

      this.rounds.forEach(async round => {
        round.steps.forEach(async step => {
          if (step.imageLocation) {
            this.queueImage(step.imageLocation);
          }
        });
      });

      return this.downloadImages();
    },

    startActivity() {
      const timeline = new TimelineMax();

      timeline
        .call(this.setupNewRound, null, null, 0.5)
        .call(this.enableKeyboard)
        .call(this.errorLoggerStart);
    },

    staggerCardsIn({ children }, onComplete) {
      TweenMax.staggerFromTo(children, 0.3, { autoAlpha: 0 }, { autoAlpha: 1, onComplete }, 0.3);
    },

    slideTextIn(textEl, onComplete) {
      TweenMax.fromTo(
        textEl,
        1,
        { autoAlpha: 0, display: 'block', x: 100 },
        { autoAlpha: 1, x: 0, onComplete }
      );
    },

    nextStep(firstStep) {
      if (this.isEndOfRound) {
        this.finishRound();
        return;
      }

      this.setCurrentStep({ firstStep });
    },

    handleCorrectKey() {
      this.sndType();
      this.incrementTypingIndex();

      if (this.isEndOfCurrentSentence) {
        this.sndBell();
        this.nextStep();
      }
    },

    finishRound() {
      if (this.isLastRound) {
        this.completeActivity();
      } else {
        this.setupNewRound();
      }
    },

    // ActivityManager Override
    completeActivity() {
      const finishTimeline = new TimelineMax({ delay: 1.25 });

      this.incrementStep();

      finishTimeline
        .call(
          () => {
            this.showAllCards = true;
          },
          null,
          null
        )
        .call(this.playCongrats, null, null, 3)
        .call(this.processEndOfActivity);
    }
  }
};
</script>

<style lang="scss">
.face-flip {
  .slide-in-transition {
    transition: all 1s ease;
  }

  .slide-in-enter {
    opacity: 0;
    transform: translateX(100px);
  }

  .slide-in-leave {
    opacity: 0;
  }

  .top {
    width: 100%;
    margin-top: 50px;
    margin-left: 20px;
    display: block;
    text-align: center;
    .card-item {
      display: inline-block;
      padding: 10px;
    }
  }

  .bottom {
    text-align: center;
    font-size: 20px;
    font-family: hwtfutura;
    display: block;
  }
}
</style>
